import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { toListRead, toRead, type CurrentRead, type ListRead, type Read } from './read';
import { readsMeta, type ReadEndpoint } from './reads';

/** Pass instead of params when the query should not run: its read is `idle`. */
export const skip: unique symbol = Symbol('skip');

const PARENT: unique symbol = Symbol('parent');

/** Params that arrive only when a parent read is ready. */
export interface Dependent<P> {
  readonly [PARENT]: Read<P>;
}

/**
 * Run the query with the parent's value once the parent is ready. Until then the child is
 * `idle` waiting on its parent, and a failed or offline parent makes the child failed or offline,
 * so "disabled until the parent is ready" is never a hand-written `enabled` flag.
 */
export function whenReady<P>(parent: Read<P>): Dependent<P> {
  return { [PARENT]: parent };
}

export type QueryInput<P> = P | Dependent<P> | typeof skip;

function isDependent<P>(input: QueryInput<P>): input is Dependent<P> {
  return typeof input === 'object' && input !== null && PARENT in input;
}

type Resolved<P> = { run: true; params: P } | { run: false; read: Read<never> };

/** The params to run with, or the read the query stands at without running. */
export function resolveInput<P>(input: QueryInput<P>): Resolved<P> {
  if (input === skip) return { run: false, read: { status: 'idle', waitingOn: 'not-requested' } };
  if (!isDependent(input)) return { run: true, params: input };
  const parent = input[PARENT];
  switch (parent.status) {
    case 'ready':
      return { run: true, params: parent.data };
    case 'failed':
    case 'offline':
      return { run: false, read: parent };
    case 'idle':
    case 'loading':
    case 'placeholder':
      return { run: false, read: { status: 'idle', waitingOn: 'parent' } };
  }
}

/**
 * What each platform supplies. The package never imports app code: web reads its campaign
 * store and the browser's online state, mobile its store and NetInfo.
 */
export interface DataLayerConfig {
  useCampaignId: () => string | null | undefined;
  useOnline: () => boolean;
  /**
   * Every query asks for one campaign's data, so until a campaign is selected each read is
   * `idle` instead of a request the server refuses for want of `X-Campaign-Id`.
   */
  requireCampaign?: boolean;
}

/**
 * The key a definition caches under: the campaign id, then the definition's own key. An
 * invalidation, `setQueryData` or `cancelQueries` for a defined query builds its key here, or it
 * misses the cache entry.
 */
export function campaignKey(campaignId: string | null | undefined, key: readonly unknown[]): unknown[] {
  return [campaignId ?? null, ...key];
}

/**
 * What a fetch is handed. `campaignId` is the id the layer put at the head of the cache key; the
 * fetch must send it as the request's `X-Campaign-Id` rather than read the campaign again, or a
 * campaign switch mid-flight caches one campaign's answer under the other's key.
 */
export interface FetchContext {
  signal: AbortSignal;
  campaignId: string | null;
}

interface QueryDefinition<P, R> {
  /** The key after the campaign id, which the layer prepends to every key. */
  key: (params: P) => readonly unknown[];
  /** Must request `context.campaignId`'s data (see {@link FetchContext}). */
  fetch: (params: P, context: FetchContext) => Promise<R>;
  /** The declared GET this fetches, so a write naming one of its entity types refreshes it. */
  reads?: ReadEndpoint;
  staleTime?: number;
}

interface InfiniteQueryDefinition<P, Page, Item, Cursor> {
  key: (params: P) => readonly unknown[];
  /** Must request `context.campaignId`'s data (see {@link FetchContext}). */
  fetch: (params: P, cursor: Cursor, context: FetchContext) => Promise<Page>;
  initialCursor: Cursor;
  /** The cursor for the page after `last`, or `undefined` when there is none. */
  nextCursor: (last: Page, pages: Page[]) => Cursor | undefined;
  items: (page: Page) => readonly Item[];
  /** The declared GET this fetches, so a write naming one of its entity types refreshes it. */
  reads?: ReadEndpoint;
  staleTime?: number;
}

export interface InfiniteRead<Item> {
  /** No `placeholder` arm: an infinite query never keeps a previous key's rows. */
  read: Exclude<ListRead<Item>, { status: 'placeholder' }>;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

export function createDataLayer(config: DataLayerConfig) {
  let definitions = 0;

  function resolveLayerInput<P>(input: QueryInput<P>, campaignId: string | null): Resolved<P> {
    if (config.requireCampaign && campaignId === null) return resolveInput<P>(skip);
    return resolveInput(input);
  }

  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious: true }): (input: QueryInput<P>) => Read<R>;
  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious?: false }): (input: QueryInput<P>) => CurrentRead<R>;
  function defineQuery<P, R>(def: QueryDefinition<P, R> & { keepPrevious?: boolean }): (input: QueryInput<P>) => Read<R> {
    const id = ++definitions;
    return function useDefinedQuery(input) {
      const campaignId = config.useCampaignId() ?? null;
      const online = config.useOnline();
      const resolved = resolveLayerInput(input, campaignId);
      const query = useQuery({
        queryKey: campaignKey(campaignId, resolved.run ? def.key(resolved.params) : ['@signalvote/data:not-run', id]),
        queryFn: ({ signal }) => def.fetch((resolved as { params: P }).params, { signal, campaignId }),
        enabled: resolved.run,
        placeholderData: def.keepPrevious ? keepPreviousData : undefined,
        staleTime: def.staleTime,
        meta: def.reads && readsMeta(def.reads),
      });
      if (!resolved.run) return resolved.read;
      return toRead(query, query.data, { online });
    };
  }

  function defineInfiniteQuery<P, Page, Item, Cursor>(
    def: InfiniteQueryDefinition<P, Page, Item, Cursor>,
  ): (input: QueryInput<P>) => InfiniteRead<Item> {
    const id = ++definitions;
    return function useDefinedInfiniteQuery(input) {
      const campaignId = config.useCampaignId() ?? null;
      const online = config.useOnline();
      const resolved = resolveLayerInput(input, campaignId);
      const query = useInfiniteQuery({
        queryKey: campaignKey(campaignId, resolved.run ? def.key(resolved.params) : ['@signalvote/data:not-run', id]),
        queryFn: ({ pageParam, signal }) =>
          def.fetch((resolved as { params: P }).params, pageParam as Cursor, { signal, campaignId }),
        initialPageParam: def.initialCursor,
        getNextPageParam: def.nextCursor,
        enabled: resolved.run,
        staleTime: def.staleTime,
        meta: def.reads && readsMeta(def.reads),
      });
      const read = (
        resolved.run ? toListRead(query, query.data?.pages.flatMap((page) => [...def.items(page)]), { online }) : resolved.read
      ) as InfiniteRead<Item>['read'];
      return {
        read,
        hasMore: resolved.run && query.hasNextPage,
        loadingMore: query.isFetchingNextPage,
        loadMore: () => {
          if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
        },
      };
    };
  }

  return { defineQuery, defineInfiniteQuery };
}
